import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { Drawer, Button, Box } from "@mui/material";
import RefundDetails from "./RefundDetails";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import {
  CircleOffIcon,
  CircleArrowUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "../../../components/commonUI/Icons/SvgIcon";
import PropTypes from "prop-types";

const RefundsTab = ({
  issueResolutions,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onReset,
  filters,
  sort,
  onSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  inputValue,
  setInputValue,
  state,
  toggleDrawer,
  RefundDetailsProps,
  currentView,
  selectedIssueResolution,
  userDetails,
  filterCount,
}) => {
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "sorting-icon sorting-icon-up";
    } else if (sort === `${key}=-1`) {
      return "sorting-icon sorting-icon-down";
    }
    return "";
  };

  const getJobTypeDisplay = (jobType) => {
    const jobTypeMap = {
      getitems: "Get items",
      housemove: "House move",
      senditems: "Send items",
      wasteDisposal: "Waste Disposal",
    };
    return jobTypeMap[jobType] || jobType;
  };

  const renderRefundRow = (issueResolution, index) => {
    const statusMap = {
      exempt: {
        label: "Waived",
        badge: "badge-secondary",
        icon: <CircleOffIcon />,
      },
      "not exempt": {
        label: "Applied",
        badge: "badge-danger",
        icon: <CircleArrowUpIcon />,
      },
      pending: {
        label: "Pending",
        badge: "badge-warning",
        icon: <ClockIcon />,
      },
      approved: {
        label: "Approved",
        badge: "badge-success",
        icon: <CheckCircleIcon />,
      },
      rejected: {
        label: "Rejected",
        badge: "badge-danger",
        icon: <XCircleIcon />,
      },
      closed: {
        label: "Cancelled by client",
        badge: "badge-danger",
      },
    };

    const status =
      issueResolution?.status ||
      issueResolution?.refund ||
      issueResolution?.jobDetails?.status;
    const statusData = statusMap[status?.toLowerCase()];

    return (
      <tr key={index}>
        <td>{issueResolution?.userDetails?.firstname}</td>
        <td>
          <span>{getJobTypeDisplay(issueResolution?.jobDetails?.jobType)}</span>
          <br />
          <span>{issueResolution?.jobDetails?.shortId}</span>
        </td>
        <td>
          <span className="u-before-icon text-capitalize">
            <strong> &nbsp;&nbsp; £</strong>
            {issueResolution?.jobDetails?.bookedFor / 100}
          </span>
        </td>
        <td className="text-capitalize">
          {issueResolution?.cancellationDetails?.reason}
        </td>
        <td>
          {statusData && (
            <span className={`badge text-capitalize ${statusData.badge}`}>
              {statusData?.icon}
              <span className="ml-1">{statusData.label}</span>
            </span>
          )}
        </td>
        <td>
          {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
            "DD-MM-YYYY"
          )}
          <br />
          {moment(issueResolution?.cancellationDetails?.cancelledOn).format(
            "HH:mm "
          )}
        </td>
        <td>
          <Button
            className="btn btn-sm btn-secondary"
            onClick={toggleDrawer("right", true, "details", issueResolution)}
          >
            Review
          </Button>
        </td>
      </tr>
    );
  };

  const columns = useMemo(
    () => [
      { key: "client", label: "Client", sortable: true, sortKey: "firstnameSorter" },
      { key: "job", label: "Job", sortable: false },
      { key: "amount", label: "Amount", sortable: true, sortKey: "amountSorter" },
      { key: "reason", label: "Reason", sortable: true, sortKey: "reasonSorter" },
      { key: "status", label: "Status", sortable: true, sortKey: "statusSorter" },
      {
        key: "requestedOn",
        label: "Requested On",
        sortable: true,
        sortKey: "requestedOnSorter",
      },
      { key: "actions", label: "Actions", sortable: false },
    ],
    []
  );

  return (
    <>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        searchPlaceholder="Client, job ID, or reason..."
        onReset={onReset}
        filters={filters}
      />

      <DataTable
        data={issueResolutions}
        columns={columns}
        renderRow={renderRefundRow}
        onSort={onSort}
        getSortClass={getSortClass}
        emptyMessage="No Refunds Found"
        colSpan={7}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={onPageChange}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={onPageSizeChange}
      />

      <Drawer
        anchor="right"
        open={state.right}
        onClose={toggleDrawer("right", false)}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          },
        }}
        className="issues-drawer"
      >
        {RefundDetailsProps("right")}
      </Drawer>
    </>
  );
};

RefundsTab.propTypes = {
  issueResolutions: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  sort: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  inputValue: PropTypes.number.isRequired,
  setInputValue: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  RefundDetailsProps: PropTypes.func.isRequired,
};

export default RefundsTab;
